import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PerformanceMonitoringTable = ({ data, onRowClick, onOptimize }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'efficiency', direction: 'desc' });
  const [filterTier, setFilterTier] = useState('all');

  const getEfficiencyColor = (score) => {
    if (score >= 90) return 'text-success bg-success/10 border-success/20';
    if (score >= 70) return 'text-warning bg-warning/10 border-warning/20';
    return 'text-error bg-error/10 border-error/20';
  };

  const getUsagePercentage = (used, quota) => {
    return Math.round((used / quota) * 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-success';
  };

  const filteredData = data
    .filter(tenant => 
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filterTier === 'all' || tenant.tier === filterTier)
    )
    .sort((a, b) => {
      if (sortConfig.direction === 'asc') {
        return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
      }
      return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc'
    });
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <Icon name="ArrowUpDown" size={14} className="opacity-50" />;
    return (
      <Icon 
        name={sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Performance Monitoring</h3>
            <p className="text-sm text-text-secondary">Per-tenant resource allocation and optimization</p>
          </div>
          
          <Button variant="outline" size="sm" iconName="Download">
            Export Report
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-sm">
            <Input
              type="search"
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Tiers</option>
            <option value="enterprise">Enterprise</option>
            <option value="professional">Professional</option>
            <option value="starter">Starter</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Tenant</span>
                  <SortIcon column="name" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('tier')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Tier</span>
                  <SortIcon column="tier" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">Storage Usage</th>
              <th className="px-6 py-3 text-left">API Calls</th>
              <th className="px-6 py-3 text-left">Bandwidth</th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('efficiency')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary"
                >
                  <span>Efficiency</span>
                  <SortIcon column="efficiency" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">Recommendations</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredData.map((tenant) => (
              <tr 
                key={tenant.id} 
                className="hover:bg-muted/50 cursor-pointer transition-colors duration-150"
                onClick={() => onRowClick?.(tenant)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Building" size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{tenant.name}</div>
                      <div className="text-sm text-text-secondary">{tenant.users} users</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`
                    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${tenant.tier === 'enterprise' ? 'bg-purple-100 text-purple-800' : 
                      tenant.tier === 'professional'? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}
                  `}>
                    {tenant.tier}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-primary">{tenant.storage.used}GB</span>
                      <span className="text-text-secondary">/ {tenant.storage.quota}GB</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          getUsageColor(getUsagePercentage(tenant.storage.used, tenant.storage.quota))
                        }`}
                        style={{ width: `${getUsagePercentage(tenant.storage.used, tenant.storage.quota)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-primary">{tenant.apiCalls.used}K</span>
                      <span className="text-text-secondary">/ {tenant.apiCalls.quota}K</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          getUsageColor(getUsagePercentage(tenant.apiCalls.used, tenant.apiCalls.quota))
                        }`}
                        style={{ width: `${getUsagePercentage(tenant.apiCalls.used, tenant.apiCalls.quota)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-primary">{tenant.bandwidth.used}GB</span>
                      <span className="text-text-secondary">/ {tenant.bandwidth.quota}GB</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          getUsageColor(getUsagePercentage(tenant.bandwidth.used, tenant.bandwidth.quota))
                        }`}
                        style={{ width: `${getUsagePercentage(tenant.bandwidth.used, tenant.bandwidth.quota)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`
                    inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border
                    ${getEfficiencyColor(tenant.efficiency)}
                  `}>
                    {tenant.efficiency}%
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {tenant.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon 
                          name={rec.type === 'upgrade' ? 'TrendingUp' : rec.type === 'optimize' ? 'Zap' : 'AlertTriangle'} 
                          size={12} 
                          className={`
                            ${rec.type === 'upgrade' ? 'text-success' : 
                              rec.type === 'optimize' ? 'text-warning' : 'text-error'}
                          `}
                        />
                        <span className="text-xs text-text-secondary">{rec.message}</span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOptimize?.(tenant);
                    }}
                    iconName="Settings"
                  >
                    Optimize
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredData.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No tenants found</h3>
          <p className="text-text-secondary">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitoringTable;